import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { easeOutExpo } from "../lib/motion";

const HERO_BG =
  "https://images.unsplash.com/photo-1596865249308-2472dc5807d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-40 md:pt-48 lg:pt-56 pb-28 md:pb-40 overflow-hidden"
    >
      {/* Background texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.10] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Radial vignette */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 70% 30%, rgba(201,162,107,0.06), transparent 60%)",
        }}
      />

      <div className="relative px-6 md:px-12 lg:px-20 max-w-[1500px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: easeOutExpo, delay: 0.1 }}
          className="mb-10"
        >
          <span className="eyebrow">Chadow Marketing — Est. for Operators</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: easeOutExpo, delay: 0.2 }}
          className="font-display text-[44px] leading-[1.02] sm:text-[64px] lg:text-[96px] xl:text-[112px] tracking-tight max-w-[15ch]"
          data-testid="hero-headline"
        >
          The backend your{" "}
          <span className="italic text-[#C9A26B]">creator business</span> needs
          to scale.
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: easeOutExpo, delay: 0.55 }}
          className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10"
        >
          <div className="lg:col-span-6 space-y-4 text-[#A8A39D] text-base md:text-lg leading-relaxed">
            <p>You&rsquo;ve built the audience.</p>
            <p>You&rsquo;ve earned the trust.</p>
            <p>You&rsquo;ve proven there&rsquo;s demand.</p>
            <p className="pt-4 border-t border-[#282930]">
              But if the business behind your content is still being held
              together manually, growth stays fragile.
            </p>
          </div>

          <div className="lg:col-span-6 lg:pl-10 lg:border-l lg:border-[#282930] flex flex-col justify-between">
            <p className="text-[#F4EFE6] text-lg md:text-xl leading-relaxed">
              Chadow Marketing builds the monetization systems, conversion
              architecture, and operational backend that turn creator momentum
              into a business with{" "}
              <span className="italic font-display text-[#C9A26B]">
                real structure
              </span>
              .
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <a
                href="#apply"
                data-testid="hero-apply-btn"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#C9A26B] text-[#0D0E12] hover:bg-[#E5BE87] transition-colors duration-300 text-[12px] tracking-[0.22em] uppercase"
              >
                Apply for Chadow Marketing
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
              <p className="text-[#A8A39D] text-xs max-w-[26ch] leading-relaxed">
                Selective support for creators with traction, demand, and room
                to scale.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Footer rule + side notes */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, ease: easeOutExpo, delay: 0.9 }}
          className="mt-24 flex items-center justify-between gap-6"
        >
          <div className="eyebrow text-[#A8A39D]">You stay front-facing</div>
          <div className="hidden md:block flex-1 mx-6 h-px bg-[#282930]" />
          <div className="eyebrow text-[#C9A26B]">
            We build behind the scenes
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
